import React from 'react'
import {
  FileText,
  Files,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  ArrowLeft,
  Download,
  Printer,
  Eye,
  Edit3,
  File,
  Calendar,
  ArrowRight,
  Mail,
  Layers,
} from 'lucide-react'

// ─── Section Wrapper ─────────────────────────────────────────────────────────
function Section({ number, title, children }) {
  return (
    <section className="space-y-4" data-section-number={number}>
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">{number}</span>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      </div>
      {children}
    </section>
  )
}

// ─── Section 1: Page Header Mockup ───────────────────────────────────────────
function PageHeaderMockup() {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Documents</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage uploaded and processed documents</p>
        </div>
      </div>
    </div>
  )
}

// ─── Section 2: Filters Mockup ───────────────────────────────────────────────
function FiltersMockup() {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search documents..."
            readOnly
            className="w-full pl-10 pr-4 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
          />
        </div>

        {/* Source Filter */}
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            disabled
            className="px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            defaultValue=""
          >
            <option value="">All Status</option>
            <option value="uploaded">Uploaded</option>
            <option value="analyzed">Analyzed</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        {/* Refresh */}
        <button className="p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-default">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Annotations */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-start gap-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-2.5">
          <Search className="h-3.5 w-3.5 mt-0.5 text-navy-600 dark:text-navy-400 flex-shrink-0" />
          <span><strong className="text-gray-700 dark:text-gray-300">Search</strong> - searches by filename; press Enter or clear to reset</span>
        </div>
        <div className="flex items-start gap-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-2.5">
          <Filter className="h-3.5 w-3.5 mt-0.5 text-navy-600 dark:text-navy-400 flex-shrink-0" />
          <span><strong className="text-gray-700 dark:text-gray-300">Status Filter</strong> - All Status, Uploaded, Analyzed, or Failed</span>
        </div>
        <div className="flex items-start gap-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-2.5">
          <RefreshCw className="h-3.5 w-3.5 mt-0.5 text-navy-600 dark:text-navy-400 flex-shrink-0" />
          <span><strong className="text-gray-700 dark:text-gray-300">Refresh</strong> - reload the documents list from the server</span>
        </div>
      </div>
    </div>
  )
}

// ─── Section 3: Documents Table Mockup ───────────────────────────────────────
function DocumentsTableMockup() {
  const docs = [
    { filename: 'Purchase_Order_PO813.pdf', type: 'application/pdf', size: '245 KB', uploaded: 'May 19, 2025', icon: 'pdf' },
    { filename: 'Invoice_INV-2024-089.pdf', type: 'application/pdf', size: '128 KB', uploaded: 'May 18, 2025', icon: 'pdf' },
    { filename: 'contract_renewal.docx', type: 'application/msword', size: '512 KB', uploaded: 'May 17, 2025', icon: 'doc' },
    { filename: 'pricing_sheet_Q2.xlsx', type: 'application/vnd.ms-excel', size: '89 KB', uploaded: 'May 16, 2025', icon: 'spreadsheet' },
    { filename: 'delivery_confirmation.eml', type: 'message/rfc822', size: '34 KB', uploaded: 'May 15, 2025', icon: 'doc' },
  ]

  const iconColor = {
    pdf: 'text-red-500',
    doc: 'text-blue-500',
    spreadsheet: 'text-green-600',
    image: 'text-green-500',
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Filename</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Size</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Uploaded</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {docs.map((doc, i) => (
              <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <FileText className={`h-5 w-5 ${iconColor[doc.icon] || 'text-gray-400'}`} />
                    <span className="font-medium text-navy-600 dark:text-navy-400">{doc.filename}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{doc.type}</td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{doc.size}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{doc.uploaded}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
          <span>Showing 1–5 of 23 results</span>
          <span className="border-l border-gray-200 dark:border-gray-700 h-4" />
          <span className="flex items-center gap-1.5">
            Rows per page
            <select disabled className="px-2 py-1 border border-gray-200 dark:border-gray-700 rounded text-sm dark:bg-gray-800 dark:text-gray-200">
              <option>10</option>
              <option>25</option>
              <option>50</option>
            </select>
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-400 rounded cursor-default opacity-50">
            <ChevronLeft className="h-4 w-4" />
            Previous
          </button>
          <button className="w-8 h-8 rounded text-sm font-medium bg-navy-800 text-white">1</button>
          <button className="w-8 h-8 rounded text-sm font-medium text-gray-600 dark:text-gray-400">2</button>
          <button className="w-8 h-8 rounded text-sm font-medium text-gray-600 dark:text-gray-400">3</button>
          <button className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 rounded cursor-default">
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Section 4: Document Detail Page Mockup ──────────────────────────────────
function DocumentDetailMockup() {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          <button className="mr-4 cursor-default">
            <ArrowLeft className="h-6 w-6 text-gray-400 dark:text-gray-500" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate">Purchase_Order_PO813.pdf</h1>
              <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">PDF Document</span>
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">Document #42</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 flex items-center gap-1.5 cursor-default">
              <Printer className="h-4 w-4" />
              Print
            </button>
            <button className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 flex items-center gap-1.5 cursor-default">
              <Download className="h-4 w-4" />
              Download
            </button>
          </div>
        </div>
      </div>

      {/* Info Cards */}
      <div className="p-5 border-b border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <FileText className="h-5 w-5 text-blue-500" />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Type</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">PDF Document</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <Calendar className="h-5 w-5 text-purple-500" />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Uploaded</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">May 19, 2025</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <File className="h-5 w-5 text-gray-500" />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Size</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">245 KB</p>
            </div>
          </div>
        </div>
      </div>

      {/* Document Preview */}
      <div className="p-5 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Document Preview</h2>
        <div className="h-48 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center border border-gray-200 dark:border-gray-700">
          <div className="text-center">
            <div className="w-24 h-32 bg-white dark:bg-gray-700 rounded-lg shadow mx-auto mb-2 flex items-center justify-center border border-gray-300 dark:border-gray-600 p-3">
              <div className="space-y-1.5 w-full">
                <div className="h-4 bg-blue-600 rounded w-2/3 ml-auto"></div>
                <div className="h-1.5 bg-gray-300 dark:bg-gray-500 rounded w-full"></div>
                <div className="h-1.5 bg-gray-300 dark:bg-gray-500 rounded w-4/5"></div>
                <div className="h-1.5 bg-gray-300 dark:bg-gray-500 rounded w-full"></div>
                <div className="h-1.5 bg-gray-300 dark:bg-gray-500 rounded w-3/5"></div>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">PDF rendered in full viewer (700px height)</p>
          </div>
        </div>
      </div>

      {/* Related Requests */}
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Related Requests (2)</h2>
          <button className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-default">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Instrument</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Issuer</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Processed Date</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">AI Confidence</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created By</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {/* Row 1 */}
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center relative text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20">
                      <FileText className="h-5 w-5" />
                      <span className="absolute -top-1 -right-1 bg-navy-600 text-white text-[10px] font-medium rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">1</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">PO-2024-0813</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Purchase order extraction</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">TechSupply Co.</td>
                <td className="px-4 py-3">
                  <p className="text-sm text-gray-900 dark:text-gray-100">May 19, 2025</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">14:32</p>
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                    Completed
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-green-500" style={{ width: '95%' }} />
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">95%</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300">JD</div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Jane Doe</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <button className="p-1.5 rounded-lg text-gray-400 cursor-default">
                    <Eye className="h-4 w-4" />
                  </button>
                </td>
              </tr>
              {/* Row 2 */}
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center relative text-orange-600 bg-orange-50 dark:text-orange-400 dark:bg-orange-900/20">
                      <FileText className="h-5 w-5" />
                      <span className="absolute -top-1 -right-1 bg-navy-600 text-white text-[10px] font-medium rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">1</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">PO-Duplicate-Check</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Re-processing verification</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">TechSupply Co.</td>
                <td className="px-4 py-3">
                  <p className="text-sm text-gray-900 dark:text-gray-100">May 20, 2025</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">09:15</p>
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                    Reviewing
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-yellow-500" style={{ width: '78%' }} />
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">78%</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300">AS</div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Alex Smith</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <button className="p-1.5 rounded-lg text-gray-400 cursor-default">
                    <Edit3 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ─── Section 7: Actions & Navigation ─────────────────────────────────────────
function ActionsAnnotationMockup() {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 max-w-2xl">
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Actions available from the Document Detail page:
      </p>
      <div className="space-y-3">
        <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="w-9 h-9 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center">
            <Download className="h-4 w-4 text-gray-600 dark:text-gray-300" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Download</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Save the original file to your device</p>
          </div>
        </div>
        <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="w-9 h-9 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center">
            <Printer className="h-4 w-4 text-gray-600 dark:text-gray-300" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Print</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Available for PDF documents only - opens the browser print dialog</p>
          </div>
        </div>
        <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="w-9 h-9 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center">
            <Edit3 className="h-4 w-4 text-gray-600 dark:text-gray-300" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Edit Request</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Open a related request in edit mode (for active statuses)</p>
          </div>
        </div>
        <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="w-9 h-9 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center">
            <ArrowLeft className="h-4 w-4 text-gray-600 dark:text-gray-300" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Back</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Navigate back to the Documents list (browser history)</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main Guide Component
// ═══════════════════════════════════════════════════════════════════════════════
export default function DocumentsGuide({ onNavigate }) {
  return (
    <div className="space-y-10">
      {/* Module Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
          <Files className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Documents</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">View and manage all uploaded documents across requests</p>
        </div>
      </div>

      {/* Section 1 */}
      <Section number="1" title="Page Header">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          The Documents page shows a list of all files uploaded to the system - attached via email ingestion, manual upload, or from processed requests.
        </p>
        <PageHeaderMockup />
      </Section>

      {/* Section 2 */}
      <Section number="2" title="Filtering & Search">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Use the search bar to find documents by filename, filter by processing status, or refresh the list.
        </p>
        <FiltersMockup />
      </Section>

      {/* Section 3 */}
      <Section number="3" title="Documents Table">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          The table displays all documents with 4 columns: <strong>Filename</strong> (clickable link to detail page), <strong>Type</strong> (MIME content type), <strong>Size</strong> (human-readable), and <strong>Uploaded</strong> date. Pagination controls appear below the table.
        </p>
        <DocumentsTableMockup />
      </Section>

      {/* Section 4 */}
      <Section number="4" title="Document Detail Page">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Click any filename in the table to open the Document Detail page. It shows metadata (type, upload date, size), a full document preview (PDF viewer or generic document viewer), and a <strong>Related Requests</strong> table listing all requests that reference this document.
        </p>
        <DocumentDetailMockup />
      </Section>

      {/* Section 5 */}
      <Section number="5" title="Available Actions">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          The Document Detail page offers several actions depending on the document and request statuses.
        </p>
        <ActionsAnnotationMockup />
      </Section>

      {/* Tips */}
      <section className="space-y-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Tips</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Search className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-xs font-semibold text-blue-900 dark:text-blue-200">Search by Filename</span>
            </div>
            <p className="text-[11px] text-blue-700 dark:text-blue-300 leading-relaxed">Type part of the filename and press Enter to filter the list. Clear the input to reset.</p>
          </div>
          <div className="rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Filter className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              <span className="text-xs font-semibold text-purple-900 dark:text-purple-200">Status Filter</span>
            </div>
            <p className="text-[11px] text-purple-700 dark:text-purple-300 leading-relaxed">Use the dropdown to quickly isolate documents that failed processing or are still being analyzed.</p>
          </div>
          <div className="rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <ArrowRight className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-xs font-semibold text-green-900 dark:text-green-200">Related Requests</span>
            </div>
            <p className="text-[11px] text-green-700 dark:text-green-300 leading-relaxed">From Document Detail, you can jump directly to any request that uses this document.</p>
          </div>
          <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Printer className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="text-xs font-semibold text-amber-900 dark:text-amber-200">Print PDFs</span>
            </div>
            <p className="text-[11px] text-amber-700 dark:text-amber-300 leading-relaxed">The Print button is only available for PDF files and opens your browser's native print dialog.</p>
          </div>
        </div>
      </section>

      {/* Related Modules */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Related Modules</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'requests', label: 'Requests', icon: FileText },
            { id: 'email-queue', label: 'Email Queue', icon: Mail },
            { id: 'templates', label: 'Templates', icon: Layers },
          ].map((mod) => {
            const Icon = mod.icon
            return (
              <button key={mod.label} onClick={() => onNavigate(mod.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                <Icon className="h-3.5 w-3.5" />
                {mod.label}
                <ArrowRight className="h-3 w-3 text-gray-400" />
              </button>
            )
          })}
        </div>
      </section>
    </div>
  )
}
